import { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Stack, Alert, Typography,
} from '@mui/material';
import { adminFetch, normalizeEmail } from '../../utils/authUtils';

/**
 * Admin-initiated "invite a friend" flow.
 *
 * Creates a Cognito user with complimentary Pro access. Cognito sends an
 * invitation email from donotreply@capitolgains.io with a temporary password;
 * on first sign-in the recipient is forced to set their own password.
 */
export default function InviteUserDialog({ open, onClose, onInvited }) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const reset = () => {
        setEmail('');
        setName('');
        setError(null);
        setSuccess(null);
    };

    const close = () => {
        reset();
        onClose();
    };

    const submit = async () => {
        setBusy(true);
        setError(null);
        setSuccess(null);
        try {
            const normalized = normalizeEmail(email);
            if (!normalized) {
                throw new Error('Email is required.');
            }
            const data = await adminFetch('/admin/users/invite', {
                email: normalized,
                name: name.trim() || undefined,
            });
            setSuccess(`${data.email} invited. Cognito is sending their invitation email now.`);
            setEmail('');
            setName('');
            if (onInvited) onInvited();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={close}>
            <DialogTitle>Invite a user</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                    Creates a new account with <strong>complimentary Pro access</strong>.
                    Cognito will email them a temporary password from{' '}
                    <code>donotreply@capitolgains.io</code>. On first sign-in, they'll be
                    prompted to set their own password.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Stack spacing={2}>
                    <TextField
                        label="Email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        fullWidth
                        size="small"
                        autoFocus
                        required
                        disabled={busy}
                    />
                    <TextField
                        label="Name (optional)"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={busy}
                        helperText="Shown in their account profile. They can change it later."
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>Close</Button>
                <Button
                    variant="contained"
                    onClick={submit}
                    disabled={busy || !email.trim()}
                >
                    {busy ? 'Sending invitation…' : 'Send invitation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
